// src/utils/thumbnailCache.js
// Manages thumbnail caching in localStorage and Supabase

const CACHE_PREFIX = 'prezlab_thumbnail_';
const CACHE_EXPIRY_DAYS = 7;

/**
 * Thumbnail cache manager
 */
export class ThumbnailCache {
  /**
   * Get thumbnail from cache (localStorage first, then Supabase)
   * @param {string} projectId - Project ID
   * @param {number} slideNumber - Slide number
   * @returns {Promise<string|null>} Cached thumbnail URL/base64 or null
   */
  static async get(projectId, slideNumber) {
    const cacheKey = `${CACHE_PREFIX}${projectId}_${slideNumber}`;

    // Try localStorage first (fastest)
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const data = JSON.parse(cached);

        // Check if expired
        const expiryDate = new Date(data.expiry);
        if (expiryDate > new Date()) {
          console.log(`✅ Thumbnail cache HIT (localStorage): slide ${slideNumber}`);
          return data.thumbnail;
        } else {
          // Expired, remove it
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (error) {
      console.warn('Error reading from thumbnail cache:', error);
    }

    // Try Supabase Storage
    try {
      const { supabaseHelpers } = await import('../lib/supabase');
      const fileName = `thumbnails/${projectId}/slide_${slideNumber}.jpg`;
      const publicUrl = await supabaseHelpers.getFileUrl('thumbnails', fileName);

      if (publicUrl) {
        console.log(`✅ Thumbnail cache HIT (Supabase): slide ${slideNumber}`);
        // Cache it locally for next time
        this.set(projectId, slideNumber, publicUrl);
        return publicUrl;
      }
    } catch (error) {
      // Supabase might not have it yet, that's okay
      console.log(`⚠️ Thumbnail not in Supabase: slide ${slideNumber}`);
    }

    console.log(`❌ Thumbnail cache MISS: slide ${slideNumber}`);
    return null;
  }

  /**
   * Save thumbnail to cache
   * @param {string} projectId - Project ID
   * @param {number} slideNumber - Slide number
   * @param {string} thumbnail - Thumbnail URL or base64
   */
  static set(projectId, slideNumber, thumbnail) {
    const cacheKey = `${CACHE_PREFIX}${projectId}_${slideNumber}`;

    try {
      const expiry = new Date();
      expiry.setDate(expiry.getDate() + CACHE_EXPIRY_DAYS);

      const data = {
        thumbnail,
        expiry: expiry.toISOString(),
        cached: new Date().toISOString()
      };

      localStorage.setItem(cacheKey, JSON.stringify(data));
      console.log(`💾 Thumbnail cached locally: slide ${slideNumber}`);
    } catch (error) {
      // localStorage might be full, that's okay
      console.warn('Error saving to thumbnail cache:', error);
    }
  }

  /**
   * Clear cache for a specific project
   * @param {string} projectId - Project ID
   */
  static clearProject(projectId) {
    const prefix = `${CACHE_PREFIX}${projectId}_`;
    const keys = Object.keys(localStorage);

    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });

    console.log(`🗑️ Cleared thumbnail cache for project ${projectId}`);
  }

  /**
   * Clear all expired thumbnails from cache
   */
  static clearExpired() {
    const keys = Object.keys(localStorage);
    const now = new Date();
    let cleared = 0;

    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          const expiryDate = new Date(data.expiry);

          if (expiryDate <= now) {
            localStorage.removeItem(key);
            cleared++;
          }
        } catch (error) {
          // Invalid data, remove it
          localStorage.removeItem(key);
          cleared++;
        }
      }
    });

    if (cleared > 0) {
      console.log(`🗑️ Cleared ${cleared} expired thumbnails from cache`);
    }
  }

  /**
   * Get cache stats
   */
  static getStats() {
    const keys = Object.keys(localStorage);
    const thumbnailKeys = keys.filter(k => k.startsWith(CACHE_PREFIX));

    let totalSize = 0;
    thumbnailKeys.forEach(key => {
      const value = localStorage.getItem(key);
      totalSize += value ? value.length : 0;
    });

    return {
      count: thumbnailKeys.length,
      sizeKB: Math.round(totalSize / 1024),
      sizeMB: (totalSize / 1024 / 1024).toFixed(2)
    };
  }
}

// Clear expired thumbnails on page load
ThumbnailCache.clearExpired();

export default ThumbnailCache;
